/**
 * LEGACY PRODUCT VARIANT SWITCHING - port of 1.4.7's ProductVariants
 *
 * snippets/product-info-legacy.liquid is Total Restroom's verbatim 1.4.7 product card. Its option
 * inputs (.product-form__single-selector) were driven by the ProductVariants class in 1.4.7's
 * theme.js. Warehouse 7.x replaced that class with the <variant-picker> custom element, which only
 * listens to inputs carrying form="<form-id>" inside a <variant-picker> - the legacy markup has
 * neither, so on 7.x clicking an option changed nothing: not the SKU, not the price, and not the
 * hidden #product-select-<id> (name="id") that add-to-cart posts, so the cart always got the variant
 * that was selected on page load.
 *
 * This restores the 1.4.7 behaviour: SKU, prices, discount label, inventory, selected value label,
 * sold-out swatch states, add-to-cart button, ?variant= in the URL and the hidden id select. It also
 * fires `variant:change` (7.x ProductSection uses it to filter the gallery) and `variant:changed`
 * (the hook documented in custom.js and used by apps).
 *
 * Listeners are delegated from the document so quick-view content injected after load works too.
 * Native 7.x selectors (snippets/product-variant-selector.liquid) share the class name but carry a
 * form attribute and sit inside <variant-picker>; they are ignored here.
 */
(() => {
  if (window.__legacyProductVariants) return;
  window.__legacyProductVariants = true;

  const formatMoney = (cents, format) => {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    format = format || window.theme.moneyFormat || '${{amount}}';

    const delimit = (number, precision, thousands = ',', decimal = '.') => {
      if (isNaN(number) || number == null) return '0';
      const [whole, fraction] = (number / 100.0).toFixed(precision).split('.');
      return whole.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`) + (fraction ? decimal + fraction : '');
    };

    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, placeholder) => {
      switch (placeholder) {
        case 'amount': return delimit(cents, 2);
        case 'amount_no_decimals': return delimit(cents, 0);
        case 'amount_with_comma_separator': return delimit(cents, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return delimit(cents, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return delimit(cents, 2, "'", '.');
        case 'amount_no_decimals_with_space_separator': return delimit(cents, 0, ' ');
        case 'amount_with_space_separator': return delimit(cents, 2, ' ', ',');
        default: return delimit(cents, 2);
      }
    });
  };

  const isLegacySelector = (element) => element.matches('.product-form__single-selector')
    && !element.hasAttribute('form')
    && !element.closest('variant-picker');

  // The card holds the selectors, the SKU/price markup and the [data-product-json] payload. Walk up
  // past any nested .card (app widgets) to the one that owns the payload.
  const getContext = (element) => {
    let card = element.closest('.card');
    while (card && !card.querySelector(':scope > [data-product-json]')) {
      card = card.parentElement && card.parentElement.closest('.card');
    }

    const jsonElement = card && card.querySelector(':scope > [data-product-json]');
    const form = card && card.querySelector('form.product-form');

    if (!jsonElement || !form) return null;

    if (!card.__legacyVariantData) {
      const json = JSON.parse(jsonElement.textContent);
      const section = card.closest('[data-section-type="product"]');
      card.__legacyVariantData = {
        product: json['product'],
        optionsWithValues: json['options_with_values'],
        inventories: json['inventories'] || {},
        currentVariantId: json['selected_variant_id'],
        options: section ? JSON.parse(section.getAttribute('data-section-settings') || '{}') : {}
      };
    }

    return { card, form, data: card.__legacyVariantData };
  };

  const getActiveOptionValues = (form) => {
    const values = [null, null, null];

    form.querySelectorAll('.product-form__single-selector').forEach((input) => {
      if (!isLegacySelector(input) || (input.type === 'radio' && !input.checked)) return;
      values[parseInt(input.getAttribute('data-option-position')) - 1] = input.value;
    });

    return values;
  };

  const findVariant = (product, [option1, option2, option3]) => product['variants'].find((variant) => {
    return variant['option1'] === option1 && variant['option2'] === option2 && variant['option3'] === option3;
  }) || null;

  const updatePrices = (card, variant) => {
    const priceList = card.querySelector('.price-list');
    if (!priceList) return;

    if (!variant) {
      priceList.style.display = 'none';
      return;
    }

    if (variant['compare_at_price'] > variant['price']) {
      priceList.innerHTML = `<span class="price price--highlight" data-money-convertible>${formatMoney(variant['price'])}</span>`
        + `<span class="price price--compare" data-money-convertible>${formatMoney(variant['compare_at_price'])}</span>`;
    } else {
      priceList.innerHTML = `<span class="price" data-money-convertible>${formatMoney(variant['price'])}</span>`;
    }

    priceList.style.display = '';
  };

  const updateSku = (card, variant) => {
    const sku = card.querySelector('.product-meta__sku-number');
    if (!sku) return;

    // Liquid omits the whole SKU line when the initial variant has no SKU, so toggle the wrapper
    const wrapper = sku.closest('.product-meta__sku') || sku;

    if (!variant || !variant['sku']) {
      wrapper.style.display = 'none';
    } else {
      sku.textContent = variant['sku'];
      wrapper.style.display = '';
    }
  };

  const updateDiscountLabel = (card, variant) => {
    const label = card.querySelector('.product-meta .product-label--on-sale');
    if (!window.theme.showDiscount || !label) return;

    if (!variant || !(variant['price'] < variant['compare_at_price'])) {
      label.style.display = 'none';
      return;
    }

    const savings = window.theme.discountMode === 'percentage'
      ? `${Math.round((variant['compare_at_price'] - variant['price']) * 100 / variant['compare_at_price'])}%`
      : `<span data-money-convertible>${formatMoney(variant['compare_at_price'] - variant['price'])}</span>`;

    label.innerHTML = window.languages.collectionOnSaleLabel.replace('{{savings}}', savings);
    label.style.display = 'inline-block';
  };

  const updateInventory = (card, data, variant) => {
    const inventoryElement = card.querySelector('.product-form__inventory');
    const inventory = variant && data.inventories[variant['id']];
    if (!data.options['showInventoryQuantity'] || !inventoryElement || !inventory) return;

    inventoryElement.classList.remove('inventory--high', 'inventory--low');

    if (variant['available']) {
      const threshold = data.options['lowInventoryThreshold'];
      const isLow = inventory['inventory_management'] !== null && inventory['inventory_policy'] === 'deny'
        && threshold > 0 && inventory['inventory_quantity'] <= threshold;
      inventoryElement.classList.add(isLow ? 'inventory--low' : 'inventory--high');
    }

    const stockCountdown = card.querySelector('.inventory-bar');
    if (stockCountdown) {
      const progress = Math.min(Math.max(inventory['inventory_quantity'] / parseInt(stockCountdown.getAttribute('data-stock-countdown-max')) * 100.0, 0), 100);
      stockCountdown.classList.toggle('inventory-bar--hidden', progress === 0);
      stockCountdown.firstElementChild.style.width = `${progress}%`;
    }

    inventoryElement.innerHTML = inventory['inventory_message'];
  };

  // Mark option values that would lead to a sold out / missing variant, given the other options
  const updateSelectors = (form, data, variant) => {
    if (!variant) return;

    form.querySelectorAll('.product-form__option[data-selector-type]').forEach((selector, i) => {
      const selectorType = selector.getAttribute('data-selector-type');
      const optionData = data.optionsWithValues[i];
      if (selectorType === 'select' || !optionData) return;

      optionData['values'].forEach((value, j) => {
        const combination = [variant['option1'], variant['option2'], variant['option3']];
        combination[i] = value;
        const match = findVariant(data.product, combination);
        const available = match ? match['available'] : false;
        const swatch = selector.querySelector(`.${selectorType}-swatch:nth-child(${j + 1})`);

        if (swatch) swatch.classList.toggle(`${selectorType}-swatch--disabled`, !available);
      });
    });
  };

  const updateAddToCartButton = (card, data, variant) => {
    const button = card.querySelector('.product-form__add-button');
    const infoList = card.querySelector('.product-form__info-list');
    if (!button) return;

    const available = variant && variant['available'];

    button.disabled = !available;
    button.classList.toggle('button--disabled', !available);
    button.classList.toggle('button--primary', !!available);

    if (!variant) {
      button.innerHTML = window.languages.productFormUnavailable;
    } else if (!available) {
      button.innerHTML = window.languages.productFormSoldOut;
    } else {
      button.innerHTML = button.hasAttribute('data-pre-order') ? window.languages.productFormPreOrder : window.languages.productFormAddToCart;
    }

    if (infoList) infoList.style.display = variant ? 'block' : 'none';

    if (data.options['showPaymentButton']) {
      const paymentButton = card.querySelector('.shopify-payment-button');
      if (paymentButton) paymentButton.style.display = available ? 'block' : 'none';
    }
  };

  const updateMasterSelector = (card, data, variant) => {
    const masterSelector = card.querySelector(`#product-select-${data.product['id']}`);
    if (!masterSelector || !variant) return;

    masterSelector.querySelectorAll('[selected]').forEach((option) => option.removeAttribute('selected'));
    const option = masterSelector.querySelector(`[value="${variant['id']}"]`);
    if (option) option.setAttribute('selected', 'selected');
    masterSelector.value = variant['id'];
  };

  const applyVariant = (context, variant) => {
    const { card, form, data } = context;
    const previousVariant = data.product['variants'].find((item) => item['id'] === data.currentVariantId) || null;

    data.currentVariantId = variant ? variant['id'] : null;

    updatePrices(card, variant);
    updateInventory(card, data, variant);
    updateSku(card, variant);
    updateDiscountLabel(card, variant);
    updateSelectors(form, data, variant);
    updateAddToCartButton(card, data, variant);
    updateMasterSelector(card, data, variant);

    if (variant && data.options['enableHistoryState']) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant['id']);
      window.history.replaceState({ path: url.toString() }, '', url.toString());
    }

    // 7.x ProductSection filters the gallery on variant:change; it reads variant options, so only
    // send it for a variant that exists
    if (variant) {
      form.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant, previousVariant } }));
    }

    form.dispatchEvent(new CustomEvent('variant:changed', { bubbles: true, detail: { variant, previousVariant } }));
  };

  document.addEventListener('change', (event) => {
    const target = event.target;
    if (!(target instanceof Element) || !isLegacySelector(target)) return;

    const context = getContext(target);
    if (!context) return;

    const selectedValue = target.closest('.product-form__option')?.querySelector('.product-form__selected-value');
    if (selectedValue) selectedValue.textContent = target.value;

    applyVariant(context, findVariant(context.data.product, getActiveOptionValues(context.form)));
  });

  const init = () => {
    document.querySelectorAll('.card > [data-product-json]').forEach((jsonElement) => {
      const context = getContext(jsonElement);
      if (!context || !context.form.querySelector('.product-form__single-selector')) return;

      // The browser can restore checked radios on back/forward navigation, which leaves them out of
      // step with the server-rendered variant - resync if so. Otherwise just set the swatch states.
      const variant = findVariant(context.data.product, getActiveOptionValues(context.form));

      if (variant && variant['id'] !== context.data.currentVariantId) {
        applyVariant(context, variant);
      } else {
        updateSelectors(context.form, context.data, variant);
      }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
